import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import cn from "classnames";
import { useEffect, useState, useMemo } from "react";

import { Header } from "features/RankingPage/Header";
import { PhotosSlider } from "features/RankingPage/PhotosSlider";
import { Text } from "shared/components/Text";
import { RankingBar } from "shared/components/RankingBar";
import { RankingEntry } from "shared/components/RankingEntry";
import { Spinner } from "shared/components/Spinner";
import { ImageOverlay } from "shared/components/ImageOverlay";
import { databaseStore } from "store/database";
import {
  getWholeResultsData,
  getTeamsData,
  getIsLoading,
} from "store/database/selectors";
import { setIsLoading } from "store/database/actions";
import {
  createResultsByMilisecondsListener,
  createResultsByReversedMilisecondsListener,
  createResultsByPointsListener,
  createResultsByMetersListener,
  createTeamsListener,
} from "shared/database";
import { prettifyMiliseconds } from "shared/utils";
import titleGlow from "assets/images/title-glow.png";
import sectionSeparator from "assets/images/section-separator.png";

import { getGameEntries } from "./games";
import css from "./styles.module.scss";

const SHOWN_GAMES_COUNT = 100;

const IMAGES_SHOWN_OFFSET = 2;

const createNewSlidingEntries = ({ currentEntryId, entries }) => {
  let newSlidingEntries = [];
  if (
    currentEntryId - IMAGES_SHOWN_OFFSET >= 0 &&
    currentEntryId + IMAGES_SHOWN_OFFSET + 1 <= entries.length
  ) {
    newSlidingEntries = entries.slice(
      currentEntryId - IMAGES_SHOWN_OFFSET,
      currentEntryId + IMAGES_SHOWN_OFFSET + 1,
    );

    return newSlidingEntries;
  }
  if (currentEntryId - IMAGES_SHOWN_OFFSET < 0) {
    if (currentEntryId - IMAGES_SHOWN_OFFSET === -1) {
      newSlidingEntries = entries.slice(
        currentEntryId - 1,
        currentEntryId + IMAGES_SHOWN_OFFSET + 1,
      );
      newSlidingEntries.unshift(entries[entries.length - 1]);

      return newSlidingEntries;
    }
    newSlidingEntries = entries.slice(
      currentEntryId,
      currentEntryId + IMAGES_SHOWN_OFFSET + 1,
    );
    newSlidingEntries.unshift(entries[entries.length - 1]);
    newSlidingEntries.unshift(entries[entries.length - 2]);

    return newSlidingEntries;
  }

  const difference = currentEntryId + IMAGES_SHOWN_OFFSET + 1 - entries.length;

  newSlidingEntries = entries.slice(
    currentEntryId - IMAGES_SHOWN_OFFSET,
    currentEntryId + 1,
  );

  if (difference === IMAGES_SHOWN_OFFSET - 1) {
    newSlidingEntries.push(entries[currentEntryId + 1]);
  }

  for (let i = 0; i < difference; ++i) {
    newSlidingEntries.push(entries[i]);
  }

  return newSlidingEntries;
};

export const RankingPage = () => {
  const { city } = useParams();

  const [entries, setEntries] = useState(null);
  const [slidingEntries, setSlidingEntries] = useState(null);
  const [currentEntryId, setCurrentEntryId] = useState(2);
  const [slideDirection, setSlideDirection] = useState("none");

  const [image, setImage] = useState(null);
  const [isImageVisible, setIsImageVisible] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (entries) {
      // + 1 needed to take into account the currentEntry element
      setSlidingEntries(
        entries.slice(
          currentEntryId - IMAGES_SHOWN_OFFSET,
          currentEntryId + IMAGES_SHOWN_OFFSET + 1,
        ),
      );
    }
  }, [entries]);

  const databaseId = useMemo(() => {
    if (entries) {
      return entries[currentEntryId].database_id;
    }
    return "";
  }, [entries, currentEntryId]);

  const results = databaseStore.useState(getWholeResultsData);
  const teamsData = databaseStore.useState(getTeamsData);
  const isLoading = databaseStore.useState(getIsLoading);

  const gameResults = useMemo(() => {
    return results[databaseId];
  }, [results, databaseId]);

  useEffect(() => {
    const fetchResults = async ({ fetchBy, reversed }) => {
      setIsLoading(true);
      if (fetchBy === "miliseconds") {
        if (reversed) {
          await createResultsByReversedMilisecondsListener({
            gameId: databaseId,
            count: SHOWN_GAMES_COUNT,
          });
        } else {
          await createResultsByMilisecondsListener({
            gameId: databaseId,
            count: SHOWN_GAMES_COUNT,
          });
        }
      } else if (fetchBy === "points") {
        await createResultsByPointsListener({
          gameId: databaseId,
          count: SHOWN_GAMES_COUNT,
        });
      } else if (fetchBy === "meters") {
        await createResultsByMetersListener({
          gameId: databaseId,
          count: SHOWN_GAMES_COUNT,
        });
      }
      setIsLoading(false);
    };

    if (databaseId && !results[databaseId]) {
      if (entries[currentEntryId].shouldCountPoints) {
        fetchResults({ fetchBy: "points" });
      } else if (entries[currentEntryId].shouldCountMeters) {
        fetchResults({ fetchBy: "meters" });
      } else if (entries[currentEntryId].shouldMilisecondsBeReversed) {
        fetchResults({ fetchBy: "miliseconds", reversed: true });
      } else {
        fetchResults({ fetchBy: "miliseconds" });
      }
    }
  }, [databaseId]);

  useEffect(() => {
    setIsLoading(true);
    document.title = "Pixel XL Rankings";
    const awaitGameEntries = async () => {
      setEntries(await getGameEntries());
      setIsLoading(false);
    };
    awaitGameEntries();
    createTeamsListener();

    //forces page to start at the top
    window.scrollTo(0, 0);
  }, []);

  const swipeLeft = () => {
    setSlideDirection("right");
    setCurrentEntryId(
      currentEntryId - 1 >= 0 ? currentEntryId - 1 : entries.length - 1,
    );
  };

  const swipeRight = () => {
    setSlideDirection("left");
    setCurrentEntryId((currentEntryId + 1) % entries.length);
  };

  if (!slidingEntries) {
    return (
      <div className={css.RankingPage}>
        <Header city={city} />
        <div className={css.mainContent}></div>
      </div>
    );
  }

  return (
    <div className={css.RankingPage}>
      <ImageOverlay
        image={image}
        isImageVisible={isImageVisible}
        setIsImageVisible={setIsImageVisible}
      />
      <Header city={city} />
      <div className={css.mainContent}>
        <PhotosSlider
          slidingEntries={slidingEntries}
          setSlideDirection={setSlideDirection}
          swipeLeft={swipeLeft}
          swipeRight={swipeRight}
          slideDirection={slideDirection}
          offset={IMAGES_SHOWN_OFFSET}
          onAnimationEnd={() => {
            setSlideDirection("none");
            let newSlidingEntries = createNewSlidingEntries({
              currentEntryId,
              entries,
            });
            setSlidingEntries(newSlidingEntries);
          }}
        />
        <div className={css.rankingContent}>
          <div className={css.rankingListHeader}>
            <Text type="white" className={css.title} bold secondaryFamily>
              {t("rankingSection.title")}
              <img src={titleGlow} alt="" className={css.titleGlow} />
            </Text>
            <div className={css.arrowButtonsContainer}>
              <button
                className={css.arrowButton}
                disabled={slideDirection !== "none"}
                onClick={swipeLeft}
              >
                <Text type="white" className={css.arrowButtonCaption} bold>
                  {"<"}
                </Text>
              </button>
              <button
                className={css.arrowButton}
                disabled={slideDirection !== "none"}
                onClick={swipeRight}
              >
                <Text type="white" className={css.arrowButtonCaption} bold>
                  {">"}
                </Text>
              </button>
            </div>
          </div>
          <img src={sectionSeparator} alt="" className={css.separator} />
          {gameResults && teamsData && !isLoading ? (
            <>
              <RankingBar
                shouldCountPoints={!!entries[currentEntryId].shouldCountPoints}
                shouldCountMeters={!!entries[currentEntryId].shouldCountMeters}
              />
              {gameResults.map((el, i) => (
                <RankingEntry
                  key={`${databaseId}${i}`}
                  place={i + 1}
                  teamName={teamsData[el.team_id].team_name}
                  points={el.points}
                  meters={el.meters}
                  image={teamsData[el.team_id].image_url}
                  setImage={setImage}
                  setIsImageVisible={setIsImageVisible}
                  shouldCountPoints={
                    !!entries[currentEntryId].shouldCountPoints
                  }
                  shouldCountMeters={
                    !!entries[currentEntryId].shouldCountMeters
                  }
                  time={prettifyMiliseconds(el.miliseconds)}
                  lighter={i % 2}
                  isInTop={i + 1 <= 3}
                />
              ))}
            </>
          ) : (
            <div className={css.spinnerContainer}>
              <Spinner type="small" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
